import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTheme } from '@mui/material'
import {
  TableComponents,
  TableVirtuoso,
  ScrollerProps,
  TableBodyProps
} from 'react-virtuoso'

import { getScrollbarSx } from 'themes'

import { default as MUITable } from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableContainer from 'components_new/atoms/Table/TableContainer'
import TableFooter from 'components_new/atoms/Table/TableFooter'
import TableHead from 'components_new/atoms/Table/TableHead'
import TableRow from 'components_new/atoms/Table/TableRow'

import BodyRow from './BodyRow'
import FooterRow from './FooterRow'
import HeaderRow from './HeaderRow'

/**
 * Data types supported by Table molecule.
 * Independent of widget-specific types for reusability.
 */
export enum TableDataType {
  ENUM = 'enum',
  NUMBER = 'number',
  STRING = 'string',
  NUMBER_TAG = 'tag_number',
  DATE = 'date'
}

/**
 * Status severity levels for table cells.
 * Used for color-coding cell values.
 */
export enum TableStatusSeverity {
  ERROR = 'error',
  WARNING = 'warning',
  SUCCESS = 'success'
}

/**
 * Status data for table cells.
 * Array of severities or null if no status.
 */
export type TableStatus = TableStatusSeverity[] | null

export type TableRow = {
  label: {
    display_label: string
    segmentDepth: number
    path: string
    hasChildren: boolean
  }
  link?: string | null
  // Optional click handler for the cell
  onCellClick?: (event: React.MouseEvent<HTMLElement>) => void
}

/**
 * Generic cell type for Table molecule.
 * All formatting and calculations should be done before creating this.
 */
export type TableCell = {
  // Display value (already formatted with unit, e.g., "1234 kr")
  label: string | string[]
  // Comparative display
  percentageDiff?: number | null
  showDiff: boolean
  // Trend tag (pre-calculated, shows percentage change indicator)
  showTrendTag?: boolean
  increaseIsPositive?: boolean
  // Styling (pre-calculated)
  color: string // e.g., "success.text" or "text.primary"
  status?: TableStatus
}

/**
 * Generic column type for Table molecule.
 * Combines column metadata with pre-formatted cell data.
 */
export type TableColumn = {
  id: string
  index: number | null // null for segment column
  label: string
  type: TableDataType
  cells: TableCell[]
  // holds the total of every row, which is pinned beside the labels
  isTotal?: boolean
  total?: string // Already formatted with unit: "1234 kr"
  // Comparison indicator for the total/summary row, mirrors TableCell's
  // percentageDiff/showTrendTag/increaseIsPositive fields.
  totalPercentageDiff?: number | null
  totalShowTrendTag?: boolean
  totalIncreaseIsPositive?: boolean
}

interface TableProps {
  activeOrder: 'asc' | 'desc'
  activeOrderBy: { id: string | null; index: number | null }
  columns: TableColumn[]
  expandedPathSet: Set<string>
  horizontal: boolean
  onSort?: (id: string, index: number | null) => void
  rows: TableRow[]
  scaleFactor: number
  segmentByName: string
  segmentMaxChars: number | null
  onToggleExpanded: (path: string, expanded: boolean) => void
  showHeader: boolean
  showTotal: boolean
  showVerticalDividers: boolean
}

const Table = (props: TableProps) => {
  const {
    activeOrder,
    activeOrderBy,
    columns,
    expandedPathSet,
    horizontal,
    onSort,
    rows,
    scaleFactor,
    segmentByName,
    segmentMaxChars,
    onToggleExpanded,
    showHeader,
    showTotal,
    showVerticalDividers
  } = props

  const longestPath = useMemo(() => {
    return rows.reduce((max, row) => Math.max(max, row.label.segmentDepth), 0)
  }, [rows])

  // The segment column is sized by its content, so a column pinned
  // beside it can only be placed once that width is measured. The
  // header is measured when it is shown, the first row otherwise.
  const [segmentWidth, setSegmentWidth] = useState(0)
  const segmentObserver = useRef<ResizeObserver | null>(null)

  const measureSegmentCell = useCallback(
    (cell: HTMLTableCellElement | null) => {
      segmentObserver.current?.disconnect()

      if (!cell) {
        return
      }

      setSegmentWidth(cell.getBoundingClientRect().width)

      segmentObserver.current = new ResizeObserver(([entry]) => {
        setSegmentWidth(entry.target.getBoundingClientRect().width)
      })

      segmentObserver.current.observe(cell)
    },
    []
  )

  useEffect(() => () => segmentObserver.current?.disconnect(), [])

  return (
    <TableVirtuoso
      data={rows}
      components={VirtuosoTableComponents}
      context={{ scaleFactor }}
      fixedHeaderContent={() => (
        <HeaderRow
          display={showHeader}
          columns={columns}
          horizontal={horizontal}
          measureSegmentCell={measureSegmentCell}
          onSort={onSort}
          order={activeOrder}
          orderBy={activeOrderBy}
          scaleFactor={scaleFactor}
          segmentBy={segmentByName}
          segmentWidth={segmentWidth}
          verticalDividers={showVerticalDividers}
        />
      )}
      itemContent={(row, item) => (
        <BodyRow
          columns={columns}
          expandedPathSet={expandedPathSet}
          longestPath={longestPath}
          item={item}
          measureSegmentCell={
            !showHeader && row === 0 ? measureSegmentCell : undefined
          }
          row={row}
          scaleFactor={scaleFactor}
          segmentMaxChars={segmentMaxChars}
          segmentWidth={segmentWidth}
          onToggleExpanded={onToggleExpanded}
          verticalDividers={showVerticalDividers}
        />
      )}
      fixedFooterContent={() =>
        !horizontal && showTotal ? (
          <FooterRow columns={columns} scaleFactor={scaleFactor} />
        ) : null
      }
    />
  )
}

export default Table

/* ----- Virtuoso Table Components ----- */

const VirtuosoTableComponents: TableComponents<TableRow> = {
  Scroller: React.forwardRef<HTMLDivElement, ScrollerProps & { context: any }>(
    (props, ref) => {
      const theme = useTheme()

      return (
        <TableContainer
          {...props}
          sx={{
            maxHeight: '100%',
            overflowX: 'auto',
            overflowAnchor: 'none', // Fix for scrolling when horizontal scroll is active.
            ...getScrollbarSx(theme, props.context?.scaleFactor)
          }}
          ref={ref}
        />
      )
    }
  ),
  Table: (props) => (
    <MUITable
      {...props}
      size="small"
      sx={{
        borderCollapse: 'separate',
        tableLayout: 'fixed',
        width: 'auto' // For auto sized, fit content cells. (Hacker).
      }}
    />
  ),
  TableBody: React.forwardRef<
    HTMLTableSectionElement,
    TableBodyProps & { context?: any }
  >(({ ...props }, ref) => <TableBody {...props} ref={ref} />),
  TableFoot: React.forwardRef((props, ref) => (
    <TableFooter {...props} ref={ref} />
  )),
  TableHead: React.forwardRef((props, ref) => (
    <TableHead {...props} ref={ref} />
  )),
  TableRow: React.forwardRef((props, ref) => (
    <TableRow {...props} hover={true} lastRowHasBorder={true} ref={ref} />
  ))
}
